/**
 * Single source of truth for chart color.
 *
 * Before this file, chart colors were defined in three disagreeing places (the
 * CSS `--chart-*` tokens, a `CHART_COLORS` hex array, and per-page literal hex).
 * Everything now imports from here. The CSS `--chart-1..5` tokens in index.css
 * are kept in sync with `CHART_PALETTE[0..4]` so any CSS-var consumer matches.
 */

/** Ordered categorical palette — distinct, accessible, brand-adjacent. */
export const CHART_PALETTE = [
  "#10b981", // emerald  (brand green)
  "#0ea5e9", // sky
  "#8b5cf6", // violet
  "#f59e0b", // amber
  "#14b8a6", // teal
  "#6366f1", // indigo
  "#ec4899", // pink
  "#f97316", // orange
  "#06b6d4", // cyan
  "#ef4444", // red
] as const;

/**
 * Semantic color per metric/series key — so "revenue" is always the same green,
 * "spend" always amber, across every page and chart.
 */
export const SERIES_COLOR: Record<string, string> = {
  revenue: "#10b981",
  net_revenue: "#10b981",
  new_customer_revenue: "#059669",
  spend: "#f59e0b",
  cost: "#f59e0b",
  reach: "#0ea5e9",
  reached_accounts: "#0ea5e9",
  impressions: "#38bdf8",
  engagements: "#8b5cf6",
  engagement: "#8b5cf6",
  orders: "#14b8a6",
  conversions: "#14b8a6",
  clicks: "#06b6d4",
  applications: "#6366f1",
  video_views: "#ec4899",
  followers_delta: "#f97316",
};

/** Color for a series key, falling back to the ordered palette by index. */
export function seriesColor(key: string, index = 0): string {
  return SERIES_COLOR[key] ?? CHART_PALETTE[index % CHART_PALETTE.length];
}

/** N colors from the ordered palette. */
export function paletteOf(n: number): string[] {
  return Array.from({ length: n }, (_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);
}
